import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, X, Video, Map, Hotel, Users, Clock, Sparkles, FileText, Calculator, Route, MessageCircle, Plane, Compass, Heart, MapPin, Leaf } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
});

const FEATURES = [
  {
    icon: Compass,
    title: 'Supporto organizzativo',
    desc: 'Indicazioni concrete basate sulla nostra esperienza diretta per organizzare il tuo viaggio al meglio.',
  },
  {
    icon: Heart,
    title: 'Consigli family friendly',
    desc: 'Suggerimenti su tappe, distanze, ritmi sostenibili, soste intelligenti e piccoli accorgimenti che fanno davvero la differenza quando si viaggia con bimbi piccoli.',
  },
  {
    icon: MapPin,
    title: 'Destinazioni testate',
    desc: 'Condividiamo solo luoghi che abbiamo visitato personalmente, con uno sguardo pratico: dove dormire, come spostarsi, cosa evitare, cosa invece vale davvero la pena.',
  },
  {
    icon: Leaf,
    title: 'Approccio autentico',
    desc: 'Un modo di viaggiare vicino alle persone e ai luoghi, lontano da resort e formule standard. Condivisione, realtà locali e contatto diretto con il territorio.',
  },
];

const FOR_WHO = [
  'Famiglie con bambini piccoli che vogliono viaggiare senza correre',
  'Genitori che desiderano organizzare un viaggio autentico e su misura',
  'Chi ha poco tempo per pianificare ma vuole farlo bene',
  'Chi cerca esperienze locali e non soluzioni standard',
  'Chi vuole sentirsi più sicuro prima di partire, con consigli concreti',
];

const NOT_FOR_WHO = [
  'Vuole itinerari fitti dalla mattina alla sera',
  'Cerca il classico "mordi e fuggi" o destinazioni di tendenza da spuntare',
  'Preferisce villaggi turistici, resort o pacchetti preconfezionati',
  'Si aspetta che prenotiamo voli, alloggi o esperienze per loro \u2013 tutte le prenotazioni devono essere effettuate autonomamente online',
  'Cerca soluzioni copia-incolla senza personalizzazione',
  'Vuole consulenza per una meta dove non siamo mai stati',
];

const PLANS = [
  {
    name: 'CONSULENZA BASE',
    price: '149',
    tagline: 'Ideale se hai già scelto la destinazione e vuoi organizzare il tuo viaggio in modo più consapevole, confrontandoti con noi, accedendo alla nostra esperienza diretta e utilizzando il nostro itinerario testato, i nostri alloggi e i contatti selezionati come base concreta per costruire il tuo viaggio in autonomia.',
    includes: [
      { icon: Video, text: 'Video call di 60 minuti in cui condivideremo:' },
      { icon: Map, text: 'Il nostro itinerario testato (come esempio pratico)' },
      { icon: Hotel, text: 'I nominativi delle strutture dove abbiamo alloggiato' },
      { icon: Users, text: 'Contatti utili (driver, guide, servizi locali se presenti)' },
      { icon: Clock, text: 'Consigli su tappe, zone e gestione dei tempi' },
      { icon: Sparkles, text: 'Chicche organizzative e suggerimenti pratici' },
      { icon: FileText, text: 'Riepilogo scritto post-call' },
    ],
    excludes: [
      'Creazione di un itinerario personalizzato su misura',
      'Ricerca di strutture ad hoc diverse da quelle da noi già testate',
      'Prenotazioni o intermediazioni',
      'Assistenza durante il viaggio',
    ],
  },
  {
    name: 'CONSULENZA COMPLETA',
    price: '299',
    tagline: 'Ideale se parti da zero e vuoi costruire il tuo viaggio con un supporto completo, passo dopo passo. Ti aiutiamo a definire destinazione, itinerario e organizzazione, condividendo la nostra esperienza diretta per progettare un viaggio su misura adatto alle tue esigenze.',
    includes: [
      { icon: Video, text: 'Video call iniziale (max 30 minuti) per raccogliere le tue esigenze' },
      { icon: Calculator, text: 'Preventivo sui costi stimati (voli, hotel, esperienze) al momento della chiamata (non garantiamo ovviamente lo stesso prezzo nelle settimane e mesi successivi in quanto non dipende da noi)' },
      { icon: Route, text: 'Creazione di un itinerario personalizzato basato sulle informazioni raccolte' },
      { icon: MessageCircle, text: 'Confronto sull\u2019itinerario proposto' },
      { icon: Plane, text: 'Ricerca e invio dei riferimenti dei voli e alloggi consigliati per prenotare in autonomia' },
      { icon: Users, text: 'Eventuali contatti utili se presenti (driver, servizi locali, guide, ecc.)' },
      { icon: FileText, text: 'PDF completo con itinerario e indicazioni pratiche' },
      { icon: Video, text: 'Video call finale (60 minuti) per spiegazione dettagliata del PDF, chiarimento di eventuali dubbi e consigli pratici su spostamenti, esperienze e attività' },
    ],
    excludes: [
      'Prenotazioni o intermediazioni',
      'Pianificazione dettagliata "minuto per minuto"',
      'Assistenza durante il viaggio',
    ],
  },
];

const Consulenza = () => (
  <div data-testid="consulenza-page">
    <Helmet>
      <title>Consulenza Viaggi con Bambini | Organizza il Tuo Viaggio — Chichi in Viaggio</title>
      <meta name="description" content="Consulenze personalizzate per organizzare viaggi in famiglia con bambini. Itinerari su misura, consigli pratici e supporto completo basato sulla nostra esperienza." />
    </Helmet>

    {/* Hero */}
    <section className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div {...fadeUp()}>
          <h1
            className="font-heading font-light text-5xl md:text-7xl tracking-tight leading-none mb-6"
            data-testid="consulenza-title"
          >
            Consulenze viaggi
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-foreground/80 mb-3" data-testid="consulenza-subtitle-1">
            Vuoi organizzare un viaggio con i tuoi bambini ma non sai da dove iniziare?
          </p>
          <p className="text-base md:text-lg leading-relaxed text-foreground/80" data-testid="consulenza-subtitle-2">
            Oppure senti che potresti farlo da sola, ma avresti bisogno di un confronto o di consigli pratici?
          </p>
        </motion.div>
      </div>
    </section>

    <div className="w-full h-px bg-border" />

    {/* 4 Features as Cards */}
    <section className="py-16 md:py-20 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            {...fadeUp(i * 0.1)}
            className="bg-card p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            data-testid={`feature-${i}`}
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 mb-5">
              <f.icon size={22} className="text-primary" />
            </span>
            <h3 className="font-serif text-2xl mb-3">{f.title}</h3>
            <p className="text-foreground/70 leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>

    {/* "Perché una consulenza?" */}
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-muted">
      <div className="max-w-4xl mx-auto">
        <motion.div {...fadeUp()} className="text-center mb-14">
          <h2 className="font-heading font-light text-4xl md:text-5xl tracking-tight mb-4">
            Perché una consulenza?
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto rounded-full" />
        </motion.div>

        <div className="space-y-10">
          <motion.div {...fadeUp(0.1)} className="flex gap-6 items-start">
            <div className="hidden md:block w-[3px] bg-primary/40 self-stretch rounded-full flex-shrink-0" />
            <p className="font-serif text-xl md:text-2xl leading-relaxed text-foreground/90">
              Viaggiare con bambini piccoli può sembrare complicato. In realtà, con la giusta organizzazione, può diventare un'esperienza <strong>meravigliosa, leggera e possibile</strong>.
            </p>
          </motion.div>

          <motion.p {...fadeUp(0.2)} className="text-lg leading-relaxed text-foreground/70 md:pl-9">
            Ti supportiamo nella pianificazione con consigli concreti, basati su viaggi che abbiamo vissuto personalmente.
          </motion.p>

          <motion.div {...fadeUp(0.3)} className="border-l-4 border-primary/60 pl-8 py-4 space-y-3">
            <p className="text-lg leading-relaxed text-foreground/80">Non progettiamo viaggi perfetti.</p>
            <p className="font-serif text-xl font-medium text-foreground">Progettiamo viaggi <em>possibili</em>.</p>
            <p className="text-lg leading-relaxed text-foreground/80">
              Aiutiamo le famiglie a organizzare esperienze locali, autentiche e sostenibili con bambini piccoli — proprio come facciamo noi, ogni volta che partiamo.
            </p>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Per chi è / Non è per chi */}
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        <motion.div {...fadeUp(0.1)} className="bg-card p-8 md:p-10 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="for-who-box">
          <h3 className="font-serif text-2xl mb-6">Per chi è questo servizio</h3>
          <ul className="space-y-4">
            {FOR_WHO.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <Check size={18} className="text-primary flex-shrink-0 mt-0.5" />
                <span className="text-foreground/70 leading-snug">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div {...fadeUp(0.2)} className="bg-card p-8 md:p-10 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="not-for-who-box">
          <h3 className="font-serif text-2xl mb-6">Non è per chi</h3>
          <ul className="space-y-4">
            {NOT_FOR_WHO.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <X size={18} className="text-foreground/40 flex-shrink-0 mt-0.5" />
                <span className="text-foreground/65 leading-snug">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>

    {/* Pricing */}
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-muted">
      <div className="max-w-5xl mx-auto">
        <motion.div {...fadeUp()} className="text-center mb-14">
          <h2 className="font-heading font-light text-4xl md:text-5xl tracking-tight mb-4">Le nostre consulenze</h2>
          <div className="w-20 h-1 bg-primary mx-auto rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start" data-testid="pricing-grid">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              {...fadeUp(i * 0.15)}
              className="bg-white border border-border rounded-2xl flex flex-col"
              data-testid={`plan-${i}`}
            >
              <div className="p-8 md:p-10 pb-6">
                <span className="font-heading font-light text-xs tracking-widest uppercase text-foreground/40">
                  {plan.name}
                </span>
                <p className="text-sm leading-relaxed mt-4 text-foreground/60">{plan.tagline}</p>
              </div>

              <div className="px-8 md:px-10 flex-1 border-t border-border pt-6">
                <p className="text-xs font-medium uppercase tracking-widest mb-5 text-foreground/35">Include</p>
                <ul className="space-y-4 mb-8">
                  {plan.includes.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.text} className="flex items-start gap-3">
                        <span className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon size={14} className="text-primary" />
                        </span>
                        <span className="text-sm leading-snug text-foreground/75 pt-1">{item.text}</span>
                      </li>
                    );
                  })}
                </ul>

                {plan.excludes.length > 0 && (
                  <>
                    <p className="text-xs font-medium uppercase tracking-widest mb-4 text-foreground/30">Non include</p>
                    <ul className="space-y-3">
                      {plan.excludes.map((item, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <X size={15} className="flex-shrink-0 mt-0.5 text-foreground/30" />
                          <span className="text-sm leading-snug text-foreground/50">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="p-8 md:p-10 pt-6">
                <Link
                  to={`/contatti?consulenza=${encodeURIComponent(plan.name)}`}
                  className="block text-center py-3.5 px-6 rounded-full text-sm font-medium transition-all duration-200 border border-primary text-primary hover:bg-primary hover:text-white"
                  data-testid={`plan-cta-${i}`}
                >
                  Richiedi informazioni
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeUp(0.3)} className="mt-10 space-y-3">
          <p className="text-sm text-foreground/60 text-center leading-relaxed">
            <strong className="text-foreground/80">Ogni mese accettiamo un numero limitato di consulenze</strong>, per garantire attenzione, ascolto e cura in ogni dettaglio.
          </p>
          <p className="text-xs text-foreground/45 text-center leading-relaxed max-w-3xl mx-auto">
            Le consulenze prevedono un servizio di supporto e condivisione di consigli, riferimenti e contatti basati sulla nostra esperienza diretta e, nel caso della Consulenza Completa su Misura, anche su ricerche aggiuntive. Non effettuiamo prenotazioni, intermediazioni o emissione di biglietti, che rimangono a carico del cliente, né operiamo come agenzia di viaggio. Non è prevista assistenza durante il viaggio.
          </p>
        </motion.div>
      </div>
    </section>
  </div>
);

export default Consulenza;
